import React from 'react';
import './VenturiLoader.css';

/**
 * VenturiLoader Component
 * Centralized, minimal, Venturi-branded loading experience.
 * Displays a centered static coffee-bean icon inside a slow-rotating circular progress arc.
 * 
 * @param {Object} props
 * @param {string} [props.size='medium'] - Loader size ('small' | 'medium' | 'large')
 * @param {string} [props.message] - Main loading message (e.g., 'Loading production data...')
 * @param {string} [props.subMessage] - Secondary supporting message (e.g., 'Preparing your production board')
 * @param {boolean} [props.fullPage=false] - Occupy full content area
 * @param {boolean} [props.overlay=false] - Render as overlay backdrop
 * @param {string} [props.className=''] - Custom container class
 */
const VenturiLoader = ({
  size = 'medium',
  message = '',
  subMessage = '',
  fullPage = false,
  overlay = false,
  className = ''
}) => {
  const containerClasses = [
    'venturi-loader-container',
    `venturi-loader-size-${size}`,
    fullPage ? 'venturi-loader-fullpage' : '',
    overlay ? 'venturi-loader-overlay' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
      role="status" 
      aria-live="polite" 
      aria-label={message || 'Loading Venturi data'}
    >
      <div className="venturi-loader-content">
        {/* Minimal circular spinner container with static centered coffee bean */}
        <div className="venturi-loader-spinner-wrapper">
          <svg className="venturi-loader-svg" width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
            {/* Stationary background track */}
            <circle cx="60" cy="60" r="50" stroke="#f1f5f9" strokeWidth="2.5" fill="none" />
            
            {/* Spinning arc + leading dot group */}
            <g className="spinning-group">
              <circle 
                cx="60" 
                cy="60" 
                r="50" 
                stroke="#2563eb" 
                strokeWidth="3" 
                strokeDasharray="90 224" 
                strokeLinecap="round" 
                fill="none" 
                transform="rotate(-90 60 60)" 
              />
              <circle cx="60" cy="10" r="4.5" fill="#2563eb" />
            </g>

            {/* Stationary centered coffee bean */}
            <g className="coffee-bean-group" transform="translate(60, 60) rotate(-35) scale(0.35)">
              <path 
                d="M0 -50 C-22 -50 -40 -30 -40 0 C-40 30 -22 50 0 50 C22 50 40 30 40 0 C40 -30 22 -50 0 -50 Z" 
                fill="#3D2314" 
              />
              <path 
                d="M0 -45 Q-12 -10 12 10 T0 45" 
                stroke="#FFFFFF" 
                strokeWidth="6" 
                strokeLinecap="round" 
                fill="none" 
              />
            </g>
          </svg>
        </div>

        {message && (
          <h3 className="venturi-loader-message">{message}</h3>
        )}

        {subMessage && (
          <p className="venturi-loader-submessage">{subMessage}</p>
        )}

        {/* Minimal capsule progress indicators */}
        <div className="venturi-loader-indicators">
          <span className="indicator active"></span>
          <span className="indicator"></span>
          <span className="indicator"></span>
        </div>
      </div>
    </div>
  );
};

export default VenturiLoader;
